import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Box,
  Badge,
  Divider,
  HStack,
  VStack,
  Flex,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { FaUser, FaUniversity, FaCalendarAlt, FaRegCopy } from "react-icons/fa";
import { getStatusInfoFromApi } from "@/utils/balanceSolicitationStatusMapper";
import { documentFormatter } from "@/utils/documentFormatter";
import { formatDateWithNoHours } from "@/utils/formatDateWithNoHours";
import { handleCopy } from "@/utils/handleCopy";

import {
  ConsigBalanceSolicitation,
  ConsigBalanceSolicitationStatusEnum,
} from "@/models/consignado/balanceSolicitation.model";

interface BalanceSolicitationDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItem: ConsigBalanceSolicitation | null;
  formatDate: (date: string) => string;
  isConsignado?: boolean;
}

export function ConsignadoBalanceDetailModal({
  isOpen,
  onClose,
  selectedItem,
  formatDate,
  isConsignado,
}: BalanceSolicitationDetailModalProps) {
  if (!selectedItem) return null;

  const bankOptions = [
    // { id: "1", name: "UY3 SOCIEDADE DE CREDITO DIRETO S/A", enumName: "UY3" },
    {
      id: "2",
      name: "ZIPDIN SOLUCOES DIGITAIS SOCIEDADE DE CREDITO DIRETO S/A",
      enumName: "ZIPDIN",
    },
    { id: "3", name: "BMP", enumName: "BMP" },
  ];

  const toast = useToast();

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(0.625rem)" />
      <ModalContent fontFamily="Lexend Deca" borderRadius="xl" boxShadow="xl">
        <ModalHeader
          borderTopRadius="xl"
          fontSize="xl"
          fontWeight="bold"
          bg="secondary"
          color="white"
        >
          <Flex justify="space-between" align="center" width="100%">
            <Text>Detalhes da Consulta</Text>
            <ModalCloseButton mt={"0.3rem"} color="white" />
          </Flex>
        </ModalHeader>
        <ModalBody py={6}>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                {selectedItem?.nome_cliente}
              </Text>
              <Text fontSize="md" color="gray.500">
                CPF: {documentFormatter(selectedItem?.cpf)}
              </Text>
            </Box>
            <Divider />
            <HStack justify="space-between">
              <Text fontWeight="medium" color="gray.600">
                Status
              </Text>
              <Badge
                bg={getStatusInfoFromApi(selectedItem?.status).colorScheme}
                color="white"
                mb={4}
                mt={2}
                px={3}
                py={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="sm"
                fontWeight="bold"
                fontFamily="Lexend Deca"
                borderRadius="md"
              >
                {getStatusInfoFromApi(selectedItem?.status).label}
              </Badge>
            </HStack>
            {selectedItem?.status !== ConsigBalanceSolicitationStatusEnum.EM_PROCESSAMENTO && (
              <HStack justify="space-between">
                <Text fontWeight="medium" color="gray.600">
                  Legenda
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {selectedItem?.descricao?.trim() || getStatusInfoFromApi(selectedItem?.status).tagLabel}{" "}
                  {selectedItem.status ===
                    ConsigBalanceSolicitationStatusEnum.PENDENTE &&
                    formatDateWithNoHours(
                      selectedItem.data_criacao.toLocaleString("pt-BR")
                    )}
                </Text>
              </HStack>
            )}
            {isConsignado &&
              selectedItem.status ==
                ConsigBalanceSolicitationStatusEnum.AGUARDANDO_AUTORIZACAO_DO_CLIENTE && (
                <HStack justify="space-between">
                  <Text fontWeight="medium" color="gray.600">
                    Termo de Autorização
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    <Flex
                      alignItems="center"
                      justifyContent={"center"}
                      flexWrap="nowrap"
                    >
                      <Text
                        color="blue.600"
                        maxW="225px"
                        onClick={() => {
                          const linkUrl = selectedItem.termo_consentimento_url ??
                            `${window.location.origin}/consignado/autorizar-consulta?balanceSolicitationId=${selectedItem.id}`;
                          if (linkUrl && linkUrl !== "Erro ao obter link") {
                            window.open(linkUrl, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        _hover={{
                          textDecoration: "underline",
                        }}
                        cursor="pointer"
                        wordBreak="break-all"
                      >
                        {selectedItem.termo_consentimento_url ??
                          `${window.location.origin}/consignado/autorizar-consulta?balanceSolicitationId=${selectedItem.id}`}
                      </Text>
                      <IconButton
                        ml={2}
                        aria-label="Copiar Link de acesso ao Termo de Autorização"
                        icon={<FaRegCopy />}
                        onClick={() => {
                          const linkUrl = selectedItem.termo_consentimento_url ??
                            `${window.location.origin}/consignado/autorizar-consulta?balanceSolicitationId=${selectedItem.id}`;
                          handleCopy(linkUrl, toast);
                        }}
                        size="sm"
                        variant="ghost"
                        color="gray.500"
                      />
                    </Flex>
                  </Text>
                </HStack>
              )}

            <Divider />

            <HStack spacing={3}>
              <FaUser size={18} color="gray.500" />
              <Text fontSize="sm" color="gray.600">
                Usuário:{" "}
                <Text as="span" fontWeight="medium">
                  {selectedItem?.nome_usuario}
                </Text>
              </Text>
            </HStack>

            <HStack spacing={3}>
              <FaUniversity size={18} color="gray.500" />
              <Text fontSize="sm" color="gray.600">
                Bancarizadora:{" "}
                <Text as="span" fontWeight="medium">
                  {bankOptions.find(
                    (bank) => bank.enumName === selectedItem?.nome_processadora
                  )?.name || "Bancarizadora não encontrada"}
                </Text>
              </Text>
            </HStack>

            <HStack spacing={3}>
              <FaCalendarAlt size={18} color="gray.500" />
              <Text fontSize="sm" color="gray.600">
                Data:{" "}
                <Text as="span" fontWeight="medium">
                  {formatDate(selectedItem?.data_criacao.toLocaleString())}
                </Text>
              </Text>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
